import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep, orderBy } from 'lodash';
import { useMutation } from '@apollo/client';
import { DASHBOARD_METER_GROUP } from '../../../../../const';
import { useBackdropContext } from '../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../context/installation';
import {
  ChangeGroupItemsOrderMutation,
  ChangeGroupItemsOrderMutationVariables,
  ChannelTypeInternal,
  DashboardItemType,
  UpdateGroupMutation,
  UpdateGroupMutationVariables,
} from '../../../../../data-access/gql-types/graphql';
import { CHANGE_GROUP_ITEMS_ORDER, UPDATE_GROUP } from '../../../../../data-access/mutations/groups';
import { useMutationErrors, useRefetchData } from '../../../../../hooks';
import { MutationErrorType } from '../../../../../types/common';
import { toastError, toastSuccess } from '../../../../../utils/toast';
import { useDevicesAndChannelsContext } from '../../../context/devices-and-channels';
import { useGroupsStore } from '../../../store/groups';
import { ChannelType } from '../../../types/channel';
import { GroupSortable, UserGroupItemSortable } from '../../group-sort-channels/types';

export const useMeasurementsDashboard = () => {
  const { t: tc } = useTranslation('common');
  const [visibleMode, setVisibleMode] = useState<boolean>(false);
  const [orderMode, setOrderMode] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [meterGroupSortable, setMeterGroupSortable] = useState<GroupSortable | null>(null);
  const { channelList } = useDevicesAndChannelsContext();
  const { selectedInstallationId } = useInstallationContext();
  const { groups } = useGroupsStore((s) => s);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { refetchGroups } = useRefetchData();
  const { handleErrors } = useMutationErrors(MutationErrorType.Group);
  const [updateGroup] = useMutation<UpdateGroupMutation, UpdateGroupMutationVariables>(UPDATE_GROUP);
  const [changeGroupItemsOrder] = useMutation<ChangeGroupItemsOrderMutation, ChangeGroupItemsOrderMutationVariables>(
    CHANGE_GROUP_ITEMS_ORDER,
  );

  useEffect(() => {
    if (groups.length) {
      const meterGroup = groups.find((g) => g.group.name === DASHBOARD_METER_GROUP);

      if (meterGroup) {
        setMeterGroupSortable({
          id: meterGroup.group.id || '',
          items: orderBy(
            meterGroup.userGroupItems.map((item) => ({
              ...item,
              id: item.itemId || '',
              resourceType: DashboardItemType.Channel,
            })),
            'order',
          ),
        });
      }
    }
  }, [groups]);

  const onChannelToggle = (id: string) => {
    setIsValid(true);

    setMeterGroupSortable((prevState) => {
      if (!prevState) return null;

      const existingItemIndex = prevState.items.findIndex((item) => item.id === id);

      if (existingItemIndex !== -1) {
        const updatedItems = [...prevState.items];
        updatedItems.splice(existingItemIndex, 1);

        return {
          ...prevState,
          items: updatedItems,
        };
      } else {
        const channelToAdd = channelList.find((ch) => ch.id === id);

        if (channelToAdd) {
          const newItem: UserGroupItemSortable = {
            itemId: id,
            id: id,
            order: prevState.items.length,
            resourceType: DashboardItemType.Channel,
            groupId: prevState.id,
            installationId: selectedInstallationId || '',
            userId: '',
          };

          return {
            ...prevState,
            items: [...prevState.items, newItem],
          };
        }

        return prevState;
      }
    });
  };

  const onVisibilitySaveFallback = () => {
    turnOffBackdrop();
    setVisibleMode(false);
    toastError({ content: tc('errors.somethingWentWrong') });
  };

  const handleVisibilitySave = () => {
    if (!isValid) {
      setIsValid(false);
      setVisibleMode(false);
      return;
    }

    if (!meterGroupSortable) {
      onVisibilitySaveFallback();
      return;
    }

    turnOnBackdrop();

    updateGroup({
      variables: {
        input: {
          installationId: selectedInstallationId,
          groupId: meterGroupSortable.id,
          name: DASHBOARD_METER_GROUP,
          groupItems: meterGroupSortable.items.map((item, index) => ({
            itemId: item.itemId || '',
            type: DashboardItemType.Channel,
            order: index,
          })),
        },
      },
      onCompleted: async (data) => {
        turnOffBackdrop();
        if (!data || !data.updateGroup) {
          onVisibilitySaveFallback();
          return;
        }

        if (data.updateGroup.result?.ok) {
          refetchGroups();
          turnOffBackdrop();
          toastSuccess({ content: tc('saved') });
          setIsValid(false);
          setVisibleMode(false);
        } else {
          turnOffBackdrop();
          handleErrors(data.updateGroup.errors || []);
        }
      },
      onError: () => onVisibilitySaveFallback(),
    });
  };

  const onOrderSaveFallback = () => {
    turnOffBackdrop();
    toastError({ content: tc('errors.somethingWentWrong') });
    setIsValid(false);
    setOrderMode(false);
  };

  const handleOrderSave = () => {
    if (!isValid) {
      setIsValid(false);
      setOrderMode(false);
      return;
    }

    if (!meterGroupSortable) {
      onOrderSaveFallback();
      return;
    }

    turnOnBackdrop();

    const items = cloneDeep(meterGroupSortable.items);

    changeGroupItemsOrder({
      variables: {
        input: {
          installationId: selectedInstallationId,
          groupId: meterGroupSortable.id,
          items: items.map((item: UserGroupItemSortable, index: number) => ({
            itemId: item.id,
            order: index,
          })),
        },
      },
      onCompleted: async (data) => {
        if (!data || !data.changeGroupItemsOrder) {
          onOrderSaveFallback();
          return;
        }

        if (data.changeGroupItemsOrder.id) {
          refetchGroups();
          turnOffBackdrop();
          toastSuccess({ content: tc('saved') });
          setIsValid(false);
          setOrderMode(false);
        } else {
          onOrderSaveFallback();
        }
      },
      onError: () => onOrderSaveFallback(),
    });
  };

  const meterChannels = channelList.filter((x) => x.data.type === ChannelTypeInternal.Meter);

  const getChannelsFromGroupSortable = () => {
    if (!meterGroupSortable) return [];

    return meterGroupSortable.items
      .map((item) => {
        const channel = channelList.find((ch) => ch.id === item.itemId);
        return channel || null;
      })
      .filter((ch) => ch !== null) as ChannelType[];
  };

  const channels = getChannelsFromGroupSortable();
  const allChannelsHidden = !channels.length && !!meterChannels.length && !visibleMode;
  const optionsVisible = !visibleMode && !orderMode && !!channels.length;

  return {
    channels,
    allChannelsHidden,
    optionsVisible,
    onChannelToggle,
    handleVisibilitySave,
    visibleMode,
    setVisibleMode,
    meterChannels,
    orderMode,
    setOrderMode,
    meterGroupSortable,
    setMeterGroupSortable,
    setIsValid,
    handleOrderSave,
  };
};
